import Loader from "components/Loader";
import React from "react";
import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import SurveyService from "services/SurveyService";
import { useUserContext } from "store/contexts/userContext";
import { surveyState } from "store/reducers/surveyReducer";
import { surveyReducer } from "store/reducers/surveyReducer";
import NoDataImg from "assets/img/no-data-found.jpg";
import { useReducer } from "react";
import CustomRangeSlider from "components/CustomRangeSlider";
import { useState } from "react";
import { useToasts } from "react-toast-notifications";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomTextArea from "components/CustomTextArea";
import CustomButton from "components/CustomButton";
import SurveySelection from "./SurveySelection";

export default () => {
  const { user } = useUserContext();
  const params = useParams();
  const location = useLocation();
  const [answers, setAnswers] = useState([]);
  const { addToast } = useToasts();
  const [reload, setReload] = useState(false);

  const [{ 
    isLoading, 
    questions, 
    activeQuestion, 
    availableSurveys, 
    selectedSurvey, 
    isTakingSurvey 
  }, dispatch] = useReducer(surveyReducer, surveyState);

  const getAnswerSchema = (questionType) => {
    if (questionType === "fixed") {
      return Yup.object().shape({
        answer: Yup.mixed().required("Please select an option.").test(
          "is-valid-option",
          "Please select an option.",
          (value) => value !== null && value !== undefined && value !== ""
        ),
      });
    }
    return Yup.object().shape({
      answer: Yup.string().trim().required("Answer is required."),
    });
  };

  useEffect(() => {
    (async () => {
      try {
        dispatch({
          type: "isLoading",
          value: true,
        });
        const resp = await SurveyService.getActiveSurveyQuestions(
          params.teamId,
          user.id
        );

        // Handle multiple surveys response
        dispatch({
          type: "setMultipleSurveys",
          value: resp.data.data,
        });
      } catch (e) {
        // Handle all error cases
        dispatch({
          type: "isLoading",
          value: false,
        });
        dispatch({
          type: "setMultipleSurveys",
          value: [],
        });
        
        if (e.response && e.response.status !== 404) {
          addToast("Failed to load surveys. Please try again.", {
            appearance: "error",
          });
        }
      }
    })();
  }, [params.teamId, user, reload, addToast]);

  // Auto-select survey from URL parameter (when navigating from timeline)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const surveyIdParam = searchParams.get('surveyId');
    
    if (surveyIdParam && availableSurveys.length > 0 && !isTakingSurvey && !selectedSurvey && !isLoading) {
      const surveyId = parseInt(surveyIdParam);
      const survey = availableSurveys.find(s => s.survey_id === surveyId);
      
      if (survey) {
        handleSelectSurvey(surveyId);
        
        // Remove the query parameter from URL to clean it up
        const newSearch = new URLSearchParams(location.search);
        newSearch.delete('surveyId');
        window.history.replaceState({}, '', `${location.pathname}${newSearch.toString() ? `?${newSearch.toString()}` : ''}`);
      } else {
        addToast("This survey is no longer available. It may have been deleted or closed.", {
          appearance: "error",
        });
        // Remove the query parameter anyway to avoid retrying
        const newSearch = new URLSearchParams(location.search);
        newSearch.delete('surveyId');
        window.history.replaceState({}, '', `${location.pathname}${newSearch.toString() ? `?${newSearch.toString()}` : ''}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, availableSurveys, isTakingSurvey, selectedSurvey, isLoading]);

  const submitSurvey = async (ans) => {
    if (!selectedSurvey || !selectedSurvey.survey_id) {
      addToast("No survey selected. Please try again.", {
        appearance: "error",
      });
      return;
    }

    const payload = {
      surveyId: selectedSurvey.survey_id,
      teamId: params.teamId,
      userId: user.id,
      answers: ans,
    };

    try {
      await SurveyService.submitSurvey(payload);
      addToast("Survey submitted successfully!", { appearance: "success" });
      
      // Mark survey as completed
      dispatch({
        type: "completeSurvey",
        value: selectedSurvey.survey_id,
      });
      
      setReload(!reload);
    } catch (error) {
      addToast("Some error occurred while saving answers.", {
        appearance: "error",
      });
    }
  };

  const handleNext = (ans) => {
    const findNext = questions.findIndex((e) => e.id === activeQuestion.id);
    if (findNext + 1 <= questions.length - 1) {
      dispatch({
        type: "setActivequestion",
        value: questions[findNext + 1] || -1,
      });
    } else if (findNext + 1 === questions.length) {
      submitSurvey(ans);
    }
  };

  const skipAnswer = () => {
    if (answers.length !== questions.length) {
      handleNext(answers);
    }
  };

  const saveAnswer = (data) => {
    if (answers.length !== questions.length) {
      let answerData;
      if (activeQuestion.type === "fixed") {
        answerData = {
          questionId: activeQuestion.id,
          optionId: parseInt(data.answer),
        };
      } else {
        answerData = {
          questionId: activeQuestion.id,
          answer: data.answer,
        };
      }
      
      const existing = [...answers, answerData];
      setAnswers(existing);
      handleNext(existing);
    }
  };

  const handleSelectSurvey = (surveyId) => {
    dispatch({
      type: "selectSurvey",
      value: surveyId,
    });
    
    dispatch({
      type: "startSurvey",
      value: surveyId,
    });
    
    setAnswers([]);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <Loader />
      </div>
    );
  }

  // Show survey selection if no survey is selected or if user wants to go back
  if (!isTakingSurvey || !selectedSurvey) {
    return (
      <SurveySelection
        surveys={availableSurveys}
        onSelectSurvey={handleSelectSurvey}
        isLoading={isLoading}
      />
    );
  }

  if (questions.length === 0 || activeQuestion === -1) {
    return (
      <div className="d-flex justify-content-center flex-column align-items-center">
        <img alt="" src={NoDataImg} width={"70%"} />
        <div>
          No worries, you will get survey questions when management will create
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="list-group list-group-flush">
        {questions.map(({ question, type, id, min_value, max_value }, iDx) =>
          id === activeQuestion?.id ? (
            <div>
              <div className="p-2 d-flex justify-content-between align-items-center">
                <h6>
                  Question {iDx + 1} of {questions.length} question(s)
                </h6>

                {questions.length > 1 && (
                  <button
                    className="d-flex justify-content-end mt-1 btn btn-link"
                    style={{ cursor: "pointer", color: "orange" }}
                    onClick={() => skipAnswer()}
                  >
                    Skip
                  </button>
                )}
              </div>
              <div className="questionArea">
                <Formik
                  initialValues={{ answer: type === "fixed" ? null : "" }}
                  validationSchema={getAnswerSchema(type)}
                  onSubmit={(values) => {
                    saveAnswer(values);
                  }}
                >
                  <Form>
                    <div className="">
                      <div className="questionTitle">{question}</div>
                      <div className="mt-3">
                        {type === "range" && (
                          <Field name="answer">
                            {({ field, form }) => {
                              const minVal = min_value !== undefined && min_value !== null ? min_value : 1;
                              const maxVal = max_value !== undefined && max_value !== null ? max_value : 10;
                              
                              const marks = {};
                              for (let i = minVal; i <= maxVal; i++) {
                                marks[i] = i;
                              }
                              
                              return (
                                <div className="mt-5">
                                  <CustomRangeSlider
                                    minValue={minVal}
                                    maxValue={maxVal}
                                    steps={1}
                                    trackStyle={{
                                      backgroundColor: "rgb(255, 161, 0)",
                                    }}
                                    dotStyle={{ borderColor: "rgb(255, 161, 0)" }}
                                    activeDotStyle={{
                                      borderColor: "rgb(255, 161, 0)",
                                      width: "10px",
                                      height: "10px",
                                      bottom: "-3px",
                                      backgroundColor: "rgb(255, 161, 0)",
                                    }}
                                    handleStyle={{
                                      border: "solid 2px rgb(255, 161, 0)",
                                      backgroundColor: "rgb(255, 161, 0)",
                                    }}
                                    marks={marks}
                                    style={{
                                      height: "40px",
                                      width: "100%",
                                      marginRight: "15px",
                                    }}
                                    value={form.values.answer}
                                    onChange={(e) =>
                                      form.setFieldValue("answer", e)
                                    }
                                  />
                                  {!!form.errors.answer &&
                                  !!form.touched.answer ? (
                                    <span className="errorMessage mt-2">
                                      {form.errors.answer}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            }}
                          </Field>
                        )}

                        {type === "text" && (
                          <Field name="answer">
                            {({ field, form }) => (
                              <CustomTextArea
                                errorClass="errorMessage"
                                textColor={"black"}
                                textAreaClass="form-control border-radius"
                                formik={form}
                                hasError={form.errors?.answer ? true : false}
                                errorMessage={form.errors?.answer}
                                placeholder="Give your answer..."
                                fieldName={`answer`}
                                rows={5}
                                defaultValue={form.values?.answer}
                              />
                            )}
                          </Field>
                        )}

                        {type === "fixed" && (
                          <Field name="answer">
                            {({ field, form }) => (
                              <div className="mt-3">
                                {activeQuestion.options && activeQuestion.options.map((option, optionIndex) => (
                                  <div key={optionIndex} className="form-check mb-2">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="answer"
                                      id={`option-${optionIndex}`}
                                      value={option.id}
                                      checked={form.values.answer === String(option.id)}
                                      onChange={(e) => form.setFieldValue("answer", e.target.value)}
                                    />
                                    <label 
                                      className="form-check-label" 
                                      htmlFor={`option-${optionIndex}`}
                                    >
                                      {option.option}
                                    </label>
                                  </div>
                                ))}
                                {form.errors.answer && form.touched.answer && (
                                  <span className="errorMessage mt-2">
                                    {form.errors.answer}
                                  </span>
                                )}
                              </div>
                            )}
                          </Field>
                        )}
                      </div>
                      <div className="mt-4 d-flex justify-content-end">
                        <CustomButton
                          buttonClass="btn btn grad_btn full_w"
                          type="submit"
                        >
                          Submit
                        </CustomButton>
                      </div>
                    </div>
                  </Form>
                </Formik>
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};
